<template>
  <div class="mt-10 rounded-[40px] bg-white p-8 pt-0 dark:bg-[#333639]">
    <Form
      @submit="oneWaySubmit"
      class="form"
      ref="oneWayTransferForm"
      :validation-schema="oneWaySchema"
      v-slot="{ errors, isSubmitting }"
    >
      <span v-if="isSubmitting">
        {{ errorFill(errors) }}
      </span>
      <div
        v-if="!utils.isEmpty(errorOnForm) && isShowModal"
        @click="closeModal"
        class="defaultModal fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-70 p-4"
      >
        <div @click.stop class="wrapper h-max w-full max-w-2xl rounded-lg">
          <div class="flex justify-end p-4">
            <button
              @click="closeModal"
              aria-label="close"
              class="closeButton inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400"
              type="button"
            >
              <svg
                class="h-5 w-5"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  clip-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  fill-rule="evenodd"
                ></path>
              </svg>
            </button>
          </div>
          <div class="p-6 pt-0">
            <p v-for="(error, index) in errors" :key="index">{{ error }}</p>
          </div>
          <div class="border-t border-gray-600 p-6">
            <button
              @click="closeModal"
              type="button"
              class="bg-main text-background rounded-[37px] px-5 py-2.5 text-center"
            >
              Ok
            </button>
          </div>
        </div>
      </div>
      <div :class="['home_form home_form-1', { active: true }]">
        <Field name="pickup" v-slot="{ field }" v-model="agencyData.pickup">
          <TrustyComplete
            clearable
            v-model="pickupRef"
            class="w-full md:w-[calc(100%/3-8px)]"
            classname="input !py-[22px] m-0 !w-full"
            @get:suggestions="fetchSuggestions"
            @update:suggestions="updateSuggestions"
            @clear:input="clearInput('pickup')"
            @select:suggestions="selectSuggestions"
            :autoCompleteString="agencyData.pickup"
            :suggestions="data"
            v-bind="field"
            placeholder="Pick Up Location*"
            data-testid="homepage-PickUpLocation"
          />
        </Field>
        <Field name="dropoff" v-slot="{ field }" v-model="agencyData.dropoff">
          <TrustyComplete
            clearable
            v-model="dropoffRef"
            class="w-full md:w-[calc(100%/3-8px)]"
            classname="input !py-[22px] m-0 !w-full"
            @get:suggestions="fetchSuggestions"
            @update:suggestions="updateSuggestions"
            @clear:input="clearInput('dropoff')"
            @select:suggestions="selectSuggestions"
            :autoCompleteString="agencyData.dropoff"
            :suggestions="data"
            v-bind="field"
            fieldType="dropoff"
            placeholder="Drop Off Location*"
            autocomplete="off"
          />
        </Field>
        <Field name="date_start" v-slot="{ field }" v-model="datePicker">
          <VueDatePicker
            ref="datePickerRef"
            :teleport-center="ssid"
            class="input m-0 text-sm md:text-lg"
            placeholder="Date / Time*"
            :class="errors.date_start ? 'error' : ''"
            :preview-format="dateFormat"
            auto-apply
            partial-flow
            :clearable="false"
            :format="dateFormat"
            :dark="mode === 'dark'"
            :light="mode === 'light'"
            v-bind="field"
            v-model="datePicker"
            @internal-model-change="handleInternal"
            :min-date="minDate"
            :min-time="minTime"
          >
            <template #right-sidebar>
              <div v-if="datePicker" class="overflow-y-scroll px-3 py-2" style="max-height: 300px">
                <div
                  v-for="time in timeOptions"
                  :key="time"
                  @click="setTime(time)"
                  :class="[
                    'time-option cursor-pointer',
                    time === selectedTime ? 'time-selected' : ''
                  ]"
                >
                  {{ time }}
                </div>
              </div>
            </template>
          </VueDatePicker>
        </Field>
        <div
          :class="{ disabled_map: disabledMap }"
          class="route_toggle inline-flex h-[64.4px] w-full min-w-[98px] cursor-pointer items-center justify-center rounded-full !bg-[#EEF2EE] dark:!bg-[#2B2D32] md:h-[69.8px] md:w-auto"
          @click="toggleRouteMap"
        >
          <LocationIcon :active="pathHasPickupAndDropOff" />
        </div>
      </div>
      <RouteMap
        v-if="showRouteMap"
        :pickupRef="pickupRef"
        :form="oneWayTransferForm"
        :dropoffRef="dropoffRef"
        @markerdragged="markerDraggedHandler"
      />
      <div class="mt-6 grid w-full grid-cols-12 gap-x-4 gap-y-10">
        <div class="col-span-12 flex flex-col lg:col-span-6">
          <p class="mb-4 text-2xl font-medium">
            Choose Vehicle Type<span class="text-red-500">*</span>
          </p>
          <Field name="car" v-model="agencyData.car">
            <div class="relative">
              <div
                @click="toggleDropdownVehicle"
                class="text-background dark:bg-background flex w-full cursor-pointer items-center justify-between rounded-[53px] border border-[#878787] bg-[#E8EDE8]/50 px-[22px] py-4 text-sm tracking-[-0.28px] outline-none dark:border-[#3D4043] dark:text-white md:text-lg"
              >
                <p v-if="!agencyData.car" class="text-[#878787]">Select</p>
                <p v-else>{{ agencyData.car.slug_class_name }}</p>
                <img class="dark:invert" src="@/assets/dli/images/select_arrow.png" alt="" />
              </div>
              <div
                v-if="isOpenVehicle"
                class="dark:bg-background absolute z-10 mt-2 h-max w-full space-y-1 rounded-[20px] border border-[#878787] bg-[#F4F6F3] px-3 py-5 text-sm text-[#878787] dark:border-[#3D4043]"
              >
                <div
                  v-for="option in cars"
                  :key="option.class_id"
                  @click="selectVehicle(option)"
                  class="hover:text-background cursor-pointer rounded-full p-3 hover:bg-[#CCF2C8] dark:hover:bg-[#CCF2C8]/5 dark:hover:text-white"
                  :class="{
                    'text-background pointer-events-none !cursor-default bg-[#CCF2C8] dark:bg-[#CCF2C8]/5 dark:text-white':
                      agencyData.car === option
                  }"
                >
                  {{ option.slug_class_name }}
                </div>
              </div>
            </div>
          </Field>
        </div>
        <div class="col-span-12 flex flex-col lg:col-span-6">
          <p class="mb-4 text-2xl font-medium">Performance<span class="text-red-500">*</span></p>
          <Field name="performance" v-model="agencyData.performance">
            <div class="relative">
              <div
                @click="toggleDropdownPerformance"
                class="text-background dark:bg-background flex w-full cursor-pointer items-center justify-between rounded-[53px] border border-[#878787] bg-[#E8EDE8]/50 px-[22px] py-4 text-sm tracking-[-0.28px] outline-none dark:border-[#3D4043] dark:text-white md:text-lg"
              >
                <p v-if="!agencyData.performance" class="text-[#878787]">Select</p>
                <p v-else>{{ agencyData.performance }}</p>
                <img class="dark:invert" src="@/assets/dli/images/select_arrow.png" alt="" />
              </div>
              <div
                v-if="isOpenPerformance"
                class="dark:bg-background absolute z-10 mt-2 h-max w-full space-y-1 rounded-[20px] border border-[#878787] bg-[#F4F6F3] px-3 py-5 text-sm text-[#878787] dark:border-[#3D4043]"
              >
                <div
                  v-for="option in performances"
                  :key="option"
                  @click="selectPerformance(option)"
                  class="hover:text-background cursor-pointer rounded-full p-3 hover:bg-[#CCF2C8] dark:hover:bg-[#CCF2C8]/5 dark:hover:text-white"
                  :class="{
                    'text-background pointer-events-none !cursor-default bg-[#CCF2C8] dark:bg-[#CCF2C8]/5 dark:text-white':
                      agencyData.performance === option
                  }"
                >
                  {{ option }}
                </div>
              </div>
            </div>
          </Field>
        </div>
        <div
          class="text-background col-span-12 text-base leading-relaxed dark:text-white md:text-2xl"
        >
          Service data
        </div>
        <div class="col-span-12 flex flex-col gap-y-4 lg:col-span-4 xl:col-span-2">
          <span class="text-background text-nowrap dark:text-white"
            >Number of passengers<span class="text-red-500">*</span></span
          >
          <Field name="number_of_passengers" v-model="agencyData.number_of_passengers">
            <input
              data-testid="passengers"
              v-model="agencyData.number_of_passengers"
              @keypress="
                (e) => {
                  // number_of_passengers CAN'T BE NULL
                  if (e.charCode >= 48 && e.charCode <= 57) {
                    return true
                  } else {
                    e.preventDefault()
                  }
                }
              "
              @input="
                // number_of_passengers CAN'T BE NULL
                (e) => {
                  if (agencyData.number_of_passengers === '') {
                    agencyData.number_of_passengers = ''
                  }
                }
              "
              type="text"
              class="text-background dark:bg-background rounded-[53px] border-[#878787] bg-[#E8EDE8]/50 px-[22px] py-4 text-sm tracking-[-0.28px] outline-none placeholder:text-[#878787] dark:border-[#3D4043] dark:text-white md:text-lg"
            />
          </Field>
          <span class="text-xs text-[#3D4043] dark:text-[#C8C8C8] md:text-sm"
            >Add any notes regarding the composition of the party in “Your notes” at the
            buttom.</span
          >
        </div>
        <div class="col-span-12 flex flex-col gap-y-4 lg:col-span-4 xl:col-span-5">
          <span class="text-background dark:text-white">Passengers Name/Data</span>
          <input
            v-model="agencyData.main_passenger"
            type="text"
            placeholder="for example: They are my spouse, fiance, family, boss, colleague, client, etc"
            class="text-background dark:bg-background rounded-[53px] border border-[#878787] bg-[#E8EDE8]/50 px-[22px] py-4 text-sm tracking-[-0.28px] outline-none placeholder:text-[#878787] dark:border-[#3D4043] dark:text-white md:text-lg"
          />
          <span class="text-xs text-[#3D4043] dark:text-[#C8C8C8] md:text-sm"
            >If you are not the passenger yourself,<br />please indicate the name(s) of the lead
            passenger(s)</span
          >
        </div>
        <div class="col-span-12 flex flex-col gap-y-4 lg:col-span-4 xl:col-span-5">
          <p class="font-medium">Language<span class="text-red-500">*</span></p>
          <Field name="other_language" v-model="agencyData.other_language">
            <div class="relative">
              <div
                @click="toggleDropdownLang"
                class="text-background dark:bg-background flex w-full cursor-pointer items-center justify-between rounded-[53px] border border-[#878787] bg-[#E8EDE8]/50 px-[22px] py-4 text-sm tracking-[-0.28px] outline-none dark:border-[#3D4043] dark:text-white md:text-lg"
              >
                <p v-if="!agencyData.other_language" class="text-[#878787]">Select</p>
                <p v-else>{{ agencyData.other_language }}</p>
                <img class="dark:invert" src="@/assets/dli/images/select_arrow.png" alt="" />
              </div>
              <div
                v-if="isOpenLang"
                class="dark:bg-background absolute z-10 mt-2 h-max w-full space-y-1 rounded-[20px] border border-[#878787] bg-[#F4F6F3] px-3 py-5 text-sm text-[#878787] dark:border-[#3D4043]"
              >
                <div
                  v-for="option in languages"
                  :key="option"
                  @click="selectLang(option)"
                  class="hover:text-background cursor-pointer rounded-full p-3 hover:bg-[#CCF2C8] dark:hover:bg-[#CCF2C8]/5 dark:hover:text-white"
                  :class="{
                    'text-background pointer-events-none !cursor-default bg-[#CCF2C8] dark:bg-[#CCF2C8]/5 dark:text-white':
                      agencyData.other_language === option
                  }"
                >
                  {{ option }}
                </div>
              </div>
            </div>
          </Field>
          <p class="text-sm text-[#C8C8C8]">
            In our company all our chauffeurs are experienced and speak English if you prefer a
            different language please indicate it, if available we will take this into account
          </p>
        </div>
        <div class="col-span-12 border-t-2 border-[#D9D9D90D]"></div>
        <div class="relative col-span-12 flex w-full flex-col gap-2 lg:col-span-6">
          <label class="text-background dark:text-white">{{ pickupSpecialText().title }}</label>
          <input
            v-model="agencyData.pickup_specific"
            type="text"
            class="text-background dark:bg-background w-full rounded-[53px] border border-[#878787] bg-[#E8EDE8]/50 px-[22px] py-4 text-sm tracking-[-0.28px] placeholder:text-[#878787] dark:border-[#3D4043] dark:text-white md:text-lg"
          />
          <span
            class="text-xs text-[#3D4043] dark:text-[#C8C8C8] md:text-sm"
            v-if="pickupSpecialText().visibleText"
          >
            If applicable please indicate arrival Flight/Train Number or GPS coordinates if needed
            (such as for Villas in the countriside or difficult to reach locations)
          </span>
        </div>
        <div
          class="col-span-12 flex w-full flex-col gap-2 max-md:border-t-2 max-md:border-t-[#DDE5DC] max-md:pt-8 dark:max-md:border-t-[#D9D9D9]/5 lg:col-span-6"
        >
          <label class="text-background dark:text-white">{{ dropoffSpecialText().title }}</label>
          <input
            v-model="agencyData.dropoff_specific"
            type="text"
            class="text-background dark:bg-background w-full rounded-[53px] border border-[#878787] bg-[#E8EDE8]/50 px-[22px] py-4 text-sm tracking-[-0.28px] placeholder:text-[#878787] dark:border-[#3D4043] dark:text-white md:text-lg"
          />
          <span
            class="text-xs text-[#3D4043] dark:text-[#C8C8C8] md:text-sm"
            v-if="dropoffSpecialText().visibleText"
          >
            If applicable please indicate departure Flight/Train Number or GPS coordinates if needed
            (such as for Villas in the countriside or difficult to reach locations)
          </span>
        </div>
        <div class="col-span-12 border-t-2 border-[#D9D9D90D]"></div>
        <div class="relative col-span-12 flex w-full flex-col justify-between gap-2 lg:col-span-6">
          <div class="flex flex-col gap-2">
            <p class="text-background text-base font-medium dark:text-white">
              Large pieces of Luggage
            </p>
            <label for="large-luggage" class="text-xs text-[#3D4043] dark:text-[#C8C8C8] md:text-sm"
              >Regular suitcases are Medium and Large Suitcases of 25-32 inches (63-81 cm). Indicate
              in “Your notes” below the number of eventual Extra Large ones, unusal size pieces,
              mobility devices, sport equipment etc.</label
            >
          </div>
          <div class="relative">
            <input
              v-model.number="luggages.large.current"
              type="text"
              id="large-luggage"
              name="large-luggage"
              class="text-background dark:bg-background w-full rounded-[53px] border-l-0 border-r-0 border-[#878787] bg-[#E8EDE8]/50 px-14 py-4 text-center text-sm tracking-[-0.28px] placeholder:text-[#878787] dark:border-[#3D4043] dark:text-white md:text-lg"
              @input="validateInput($event, 'large')"
            />

            <button
              @click="decrementValue('large')"
              type="button"
              class="bg-main dark:text-main absolute left-1 top-1 flex size-11 items-center justify-center rounded-full border border-transparent font-sans text-4xl font-extralight text-white duration-300 hover:scale-95 dark:border-[#3D4043] dark:bg-[#1E1E20] dark:text-[#5FD052] md:left-1.5 md:top-1.5 md:size-[51px]"
            >
              <p class="absolute bottom-0 left-0 right-0 top-0 h-[1px]">–</p>
            </button>
            <button
              data-testid="button-plus"
              @click="incrementValue('large')"
              type="button"
              class="bg-main dark:text-main absolute right-1 top-1 flex size-11 origin-center items-center justify-center rounded-full border border-transparent font-sans text-4xl font-extralight text-white duration-300 hover:scale-95 dark:border-[#3D4043] dark:bg-[#1E1E20] dark:text-[#5FD052] md:right-1.5 md:top-1.5 md:size-[51px]"
            >
              <p class="absolute bottom-0 left-0 right-0 top-0 h-[1px]">+</p>
            </button>
          </div>
        </div>
        <div class="relative col-span-12 flex w-full flex-col justify-between gap-2 lg:col-span-6">
          <div class="flex flex-col gap-2">
            <p class="text-background text-base font-medium dark:text-white">
              Small pieces of Luggage
            </p>
            <label for="small-luggage" class="text-xs text-[#3D4043] dark:text-[#C8C8C8] md:text-sm"
              >Small or Carry on luggage shoud not exceed 22 inches (55 cm). No need to indicate
              hadbags, computer bags or small packpacks.</label
            >
          </div>
          <div class="relative">
            <input
              v-model.number="luggages.small.current"
              type="text"
              id="small-luggage"
              name="small-luggage"
              class="text-background dark:bg-background w-full rounded-[53px] border-l-0 border-r-0 border-[#878787] bg-[#E8EDE8]/50 px-14 py-4 text-center text-sm tracking-[-0.28px] placeholder:text-[#878787] dark:border-[#3D4043] dark:text-white md:text-lg"
              @input="validateInput($event, 'small')"
            />

            <button
              @click="decrementValue('small')"
              type="button"
              class="bg-main dark:text-main absolute left-1 top-1 flex size-11 items-center justify-center rounded-full border border-transparent font-sans text-4xl font-extralight text-white duration-300 hover:scale-95 dark:border-[#3D4043] dark:bg-[#1E1E20] dark:text-[#5FD052] md:left-1.5 md:top-1.5 md:size-[51px]"
            >
              <p class="absolute bottom-0 left-0 right-0 top-0 h-[1px]">–</p>
            </button>
            <button
              @click="incrementValue('small')"
              type="button"
              class="bg-main dark:text-main absolute right-1 top-1 flex size-11 items-center justify-center rounded-full border border-transparent font-sans text-4xl font-extralight text-white duration-300 hover:scale-95 dark:border-[#3D4043] dark:bg-[#1E1E20] dark:text-[#5FD052] md:right-1.5 md:top-1.5 md:size-[51px]"
            >
              <p class="absolute bottom-0 left-0 right-0 top-0 h-[1px]">+</p>
            </button>
          </div>
        </div>
        <div class="col-span-12 flex w-full flex-col gap-2">
          <span class="mb-2 text-xs text-[#3D4043] dark:text-white md:text-[16px]">Your notes</span>
          <input
            v-model="agencyData.notes"
            type="text"
            class="text-background dark:bg-background w-full rounded-[53px] border border-[#878787] bg-[#E8EDE8]/50 px-[22px] py-4 text-sm tracking-[-0.28px] placeholder:text-[#878787] dark:border-[#3D4043] dark:text-white md:text-lg"
          />
          <span class="text-xs text-[#3D4043] dark:text-[#C8C8C8] md:text-sm">
            Type here any note or additional instruction which would help in correctly performing
            the service, including additional data on party composition, types of suitcases or any
            requirement.
          </span>
        </div>

        <div class="col-span-12 flex flex-col gap-y-4 lg:col-span-6">
          <p class="text-2xl font-medium">Ride price</p>
          <input
            @input="agencyData.amount = agencyData.amount.replace(/\D/g, '')"
            placeholder="Enter the amount"
            v-model="agencyData.amount"
            type="text"
            class="text-background dark:bg-background w-full rounded-[53px] border-l-0 border-r-0 border-[#878787] bg-[#E8EDE8]/50 px-5 py-4 text-sm tracking-[-0.28px] placeholder:text-[#878787] dark:border-[#3D4043] dark:text-white md:text-lg"
          />
        </div>
        <div class="col-span-12 flex flex-col gap-y-4 lg:col-span-6">
          <p class="text-2xl font-medium">File number</p>
          <input
            v-model="agencyData.file_number"
            type="text"
            class="text-background dark:bg-background w-full rounded-[53px] border-l-0 border-r-0 border-[#878787] bg-[#E8EDE8]/50 px-5 py-4 text-sm tracking-[-0.28px] placeholder:text-[#878787] dark:border-[#3D4043] dark:text-white md:text-lg"
          />
        </div>
      </div>
      <div class="next_step_button_wrapper !mt-10 !justify-end">
        <button type="submit" class="next_step_button" :disabled="loading || !agencyData.car">
          Next
        </button>
      </div>
    </Form>
  </div>
</template>

<script setup>
import LocationIcon from '@/components/ui/icons/LocationIcon.vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import { Field, Form } from 'vee-validate'

import moment from 'moment'
import * as yup from 'yup'

import { computed, inject, onBeforeMount, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { useMobile } from '@/compose/ismobile'
import { useDatePicker } from '@/compose/datePicker'
import { useCarsStore, useMainStore, useOrderStore } from '@/stores'
import { storeToRefs } from 'pinia'
import { useFetcher } from '@/compose/axios'
import { useUserStore } from '@/stores/user'
import { useTrustyStore } from '@/stores/data/trustyComplete'
import TrustyComplete from '@/components/ui/autocomplete/TrustyComplete.vue'
import RouteMap from '@/components/features/map/RouteMap.vue'

const props = defineProps({
  handleKeyDown: Function
})

const oneWayTransferForm = ref(null)

const selectedTime = ref('')

const carsStore = useCarsStore()
const trustyStore = useTrustyStore()
const userStore = useUserStore()
const { agencyData } = storeToRefs(userStore)

const { cars } = storeToRefs(carsStore)
const { dropoffRef, pickupRef, data, pathStartFinish, mainTypes } = storeToRefs(trustyStore)
const { fetchSuggestions, selectSuggestions, updateSuggestions, clearInput } = trustyStore
const { axiosInstance } = useFetcher({ baseUrl: import.meta.env.VITE_APP_API_URL })

const {
  datePickerRef,
  minDate,
  minTime,
  datePicker,
  timeOptions,
  dateFormat,
  handleInternal,
  initialDate,
  timeSetFirstTime
} = useDatePicker()

const { isMobile } = useMobile()
const router = useRouter()
const utils = inject('utils')
const regexLink = inject('regexLink')
const regexIsHttps = inject('regexIsHttps')
const performances = ref([
  'Chauffeur',
  'Driver/Guide',
  'Specialized Driver/Guide',
  'Assistant/Tour Escort',
  'Tour Guide'
])
const languages = ref(['English', 'Italian', 'Spanish'])
const luggages = ref({
  large: {
    current: 0,
    max: 30
  },
  small: {
    current: 0,
    max: 30
  }
})
const isOpenVehicle = ref(false)
const isOpenPerformance = ref(false)
const isOpenLang = ref(false)
const toggleDropdownVehicle = () => {
  isOpenVehicle.value = !isOpenVehicle.value
}
const toggleDropdownPerformance = () => {
  isOpenPerformance.value = !isOpenPerformance.value
}
const toggleDropdownLang = () => {
  isOpenLang.value = !isOpenLang.value
}

const showRouteMap = ref(false)
const isShowModal = ref(true)
const errorOnForm = ref(null)
const markersMoved = ref({
  pickup: false,
  dropoff: false
})
const disabledMap = ref(false)

const mainStore = useMainStore()
const orderStore = useOrderStore()
const { orderData } = storeToRefs(orderStore)
const { mode, ssid } = storeToRefs(mainStore)
const loading = ref(false)

const setTime = (time) => {
  const [hours, minutes] = time.split(':').map(Number)
  if (initialDate.value) {
    let updatedDate = new Date(initialDate.value)
    updatedDate.setHours(hours, minutes)
    datePicker.value = updatedDate
    orderData.value.date_start = moment(updatedDate).format('YYYY-MM-DD HH:mm')
  }
  selectedTime.value = time

  datePickerRef.value.closeMenu()
}

const pickupSpecialText = () => {
  if (orderData.value.mainTypes.pickup == 'airport')
    return { title: 'Arrival Flight information', visibleText: false }
  if (
    orderData.value.mainTypes.pickup == 'train_station' ||
    orderData.value.mainTypes.pickup == 'transit_station'
  )
    return { title: 'Arrival Train Information', visibleText: false }
  if (orderData.value.mainTypes.pickup == 'port')
    return { title: 'Arrival Ship Information', visibleText: false }
  return { title: 'Pick Up Location Specifics', visibleText: true }
}

const dropoffSpecialText = () => {
  if (orderData.value.mainTypes.dropoff == 'airport')
    return { title: 'Departure Flight information', visibleText: false }
  if (
    orderData.value.mainTypes.dropoff == 'train_station' ||
    orderData.value.mainTypes.dropoff == 'transit_station'
  )
    return { title: 'Departure Train Information', visibleText: false }
  if (orderData.value.mainTypes.dropoff == 'port')
    return { title: 'Departure Ship Information', visibleText: false }
  return { title: 'Drop Off Location Specifics', visibleText: true }
}

const validateInput = (event, type) => {
  const cleanedValue = event.target.value.replace(/\D/g, '')
  luggages.value[type].current = Math.min(cleanedValue, luggages.value[type].max)
}

function decrementValue(type) {
  const newValue = parseInt(luggages.value[type].current) - 1
  luggages.value[type].current = newValue >= 0 ? newValue : 0
}

const selectVehicle = (option) => {
  agencyData.value.car = option
  carsStore.selectCar(option)
  isOpenVehicle.value = false
}

const selectPerformance = (option) => {
  agencyData.value.performance = option
  isOpenPerformance.value = false
}

const selectLang = (option) => {
  agencyData.value.other_language = option
  isOpenLang.value = false
}

function incrementValue(type) {
  const newValue = parseInt(luggages.value[type].current) + 1
  luggages.value[type].current =
    newValue <= luggages.value[type].max ? newValue : luggages.value[type].max
}

const pathHasPickupAndDropOff = computed(() => {
  return pathStartFinish.value.valid.pickup && pathStartFinish.value.valid.dropoff
})

const errorFill = (errorBag) => {
  errorOnForm.value = errorBag
}

const markerDraggedHandler = (locationType) => {
  markersMoved.value[locationType] = true
}

watch(errorOnForm, (newVal) => {
  if (!utils.isEmpty(newVal)) {
    isShowModal.value = true
  }
})

function closeModal() {
  isShowModal.value = false
}

const toggleRouteMap = () => {
  if (disabledMap.value) return
  showRouteMap.value = !showRouteMap.value
}

watch(
  () => [pathStartFinish.value.valid.pickup, pathStartFinish.value.valid.dropoff],
  ([updatedPickupValid, updatedDropoffValid]) => {
    const isValid =
      updatedPickupValid && updatedDropoffValid && Object.keys(pathStartFinish.value).length > 1
    showRouteMap.value = isValid
  },
  { immediate: true }
)

watch(mainTypes, (value) => {
  orderStore.update({
    mainTypes: value
  })
})

const oneWaySchema = yup.object({
  pickup: yup
    .string()
    .required('Pick up is a required field')
    .max(230)
    .test(
      'location-restricted',
      'The airport is located at:  Amerigo Vespucci Airport, Via del Termine, Florence, Metropolitan City of Florence, Italy',
      (value) => !value.toString().includes('Amerigo Vespucci Airport, Viale Belfiore')
    )
    .test(
      'google-complete',
      'Address must be verified using Google Autocomplete, and the country must be within the European Union or Switzerland',
      () => {
        if (orderData.value.ride_history || orderData.value.fleet) return true
        return pathStartFinish.value.valid.pickup
      }
    )
    .matches(regexIsHttps, { excludeEmptyString: true })
    .matches(regexLink, { excludeEmptyString: true }),

  dropoff: yup
    .string()
    .required('Drop Off is a required field')
    .max(230, 'Drop Off should not exceed 230 character')
    .test(
      'location-restricted',
      'The airport is located at:  Amerigo Vespucci Airport, Via del Termine, Florence, Metropolitan City of Florence, Italy',
      (value) => !value.toString().includes('Amerigo Vespucci Airport, Viale Belfiore')
    )
    .test(
      'google-complete',
      'Address must be verified using Google Autocomplete, and the country must be within the European Union or Switzerland',
      () => {
        if (orderData.value.ride_history || orderData.value.fleet) return true
        return pathStartFinish.value.valid.dropoff
      }
    )
    .matches(regexIsHttps, { excludeEmptyString: true })
    .matches(regexLink, { excludeEmptyString: true }),
  number_of_passengers: yup.string().required('Number of passengers is a required field'),
  date_start: yup
    .string()
    .required('Date is a required field')
    .max(230, 'Date should not exceed 230 character')
    .matches(regexIsHttps, { excludeEmptyString: true })
    .matches(regexLink, { excludeEmptyString: true }),

  car: yup.object().required('Vehicle type is required'),

  performance: yup.string().required('Performance selection is required'),

  main_passenger: yup.string().nullable(),

  other_language: yup.string().required('Language selection is required'),

  pickup_specific: yup.string().nullable(),
  dropoff_specific: yup.string().nullable(),
  notes: yup.string().nullable(),

  file_number: yup.string().nullable()
})

const oneWaySubmit = async (values) => {
  if (loading.value) return
  loading.value = true
  try {
    // Сохранить UTM-метки перед сбросом
    const utmData = orderStore.getUtmData()

    values.date_start = moment(values.date_start || datePicker.value).format('YYYY-MM-DD HH:mm')
    const orderMainTypes = orderData.value.mainTypes
    const payload = {
      pickup: agencyData.value.pickup,
      dropoff: agencyData.value.dropoff,
      date_start: values.date_start,
      hours: agencyData.value.hours || null,
      car: agencyData.value.car.class_id,
      performance: agencyData.value.performance,
      number_of_passengers: parseInt(agencyData.value.number_of_passengers),
      main_passenger: agencyData.value.main_passenger || null,
      other_language: agencyData.value.other_language,
      pickup_specific: agencyData.value.pickup_specific || null,
      dropoff_specific: agencyData.value.dropoff_specific || null,
      number_suitcases: `Large: ${luggages.value.large.current}; Small: ${luggages.value.small.current}`,
      notes: agencyData.value.notes || null,
      amount: parseInt(agencyData.value.amount) || 0,
      file_number: agencyData.value.file_number || null,
      type_of_service: 'oneWayTransfer',
      status: 2,
      mainTypes: orderMainTypes,
      ...utmData // Добавить UTM-метки в payload
    }

    orderStore.$reset()
    orderStore.update(payload)

    const response = await axiosInstance.post('/agency/order/create', payload)
    const { order, transaction } = response.data.data

    if (!transaction || !transaction.transaction_id) {
      console.error('❌ transaction error:', transaction)
      return
    }

    orderStore.update({
      ...order,
      transaction_id: transaction.transaction_id,
      countdown: null,
      status: 2,
      allowedPages: {
        payment: 1
      }
    })

    orderStore.updateOrderId(order.id)
    await router.push('/payment/' + transaction.transaction_id)
  } catch (err) {
    console.error('Ошибка при создании заказа или транзакции:', err)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  axiosInstance
    .get('/cars')
    .then(function (response) {
      carsStore.update(response.data.data)
    })
    .catch(function (error) {
      console.log(error)
    })
  if (pathStartFinish.value.valid.pickup && pathStartFinish.value.valid.dropoff) {
    showRouteMap.value = true
  }

  if (orderData.value.date_start) {
    timeSetFirstTime.value = false
    datePicker.value = new Date(orderData.value.date_start)
  }
  orderStore.update({ status: 2, type_of_service: 'oneWayTransfer' })

  if (isMobile.value) {
    document.addEventListener('keydown', props.handleKeyDown)
  }
})

onUnmounted(() => {
  if (isMobile.value) {
    document.removeEventListener('keydown', props.handleKeyDown)
  }
})

onBeforeMount(() => {})
</script>

<style>
.disabled_map {
  cursor: not-allowed !important;
}
</style>

<style>
.disabled_map {
  cursor: not-allowed !important;
}
</style>
